import { useEffect, useRef, useCallback } from "react";

// Time constant (ms) for the eased wheel animation: higher = floatier.
const WHEEL_EASE_TAU = 90;
// How long the wheel must be idle before we settle on a slot boundary.
const WHEEL_IDLE_MS = 140;
// Fallback settle delay for browsers without the scrollend event.
const SCROLL_IDLE_MS = 180;

// Binary search helpers over the stable slot geometry (slots sorted by left/right).
function findStartIndex(slots, x) {
  // first slot whose right >= x
  let lo = 0;
  let hi = slots.length - 1;
  let ans = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (slots[mid].right >= x) {
      ans = mid;
      hi = mid - 1;
    } else {
      lo = mid + 1;
    }
  }
  return ans;
}

function findEndIndex(slots, x) {
  // last slot whose left <= x
  let lo = 0;
  let hi = slots.length - 1;
  let ans = slots.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (slots[mid].left <= x) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

// Returns true when a wheel event is very likely produced by a trackpad rather
// than a physical mouse wheel. Imperfect but standard: trackpads emit
// pixel-mode deltas, often with a horizontal component and fine-grained values,
// whereas mouse wheels emit coarse line/page steps.
function isLikelyTrackpad(e) {
  if (e.deltaMode !== 0) return false; // LINE/PAGE mode => real mouse wheel
  if (e.deltaX !== 0) return true; // any horizontal component => trackpad
  return Math.abs(e.deltaY) < 50; // mouse wheels emit coarse ~120/100-step deltas
}

// Whether the document itself has room to scroll vertically. When it does not,
// a vertical wheel gesture would be a no-op (macOS mouse wheels emit small
// pixel deltas that look like a trackpad), so the timeline is free to claim it.
function pageScrollsVertically() {
  const doc = document.documentElement;
  return doc.scrollHeight > doc.clientHeight + 1;
}

// Encapsulates: native horizontal scroll windowing (which slots to mount),
// device-aware wheel-to-horizontal translation, and edge proximity detection
// for dynamic universe extension.
// `scrollerEl` (state-backed) drives listener attachment: if the scroller DOM
// node is ever unmounted and remounted (e.g. a transient "no access" render
// during first-login hydration), the effects re-run and re-attach to the live
// element instead of staying bound to the detached one.
export default function useTimelineScroll({
  scrollerRef,
  scrollerEl,
  getSlots,
  onWindowChange,
  onNearEdge,
  onMetrics,
  buffer = 900,
  edgeThreshold = 700,
}) {
  const rafId = useRef();
  const lastWindow = useRef({ start: -1, end: -1 });
  const getSlotsRef = useRef(getSlots);
  const onWindowChangeRef = useRef(onWindowChange);
  const onNearEdgeRef = useRef(onNearEdge);
  const onMetricsRef = useRef(onMetrics);

  useEffect(() => {
    getSlotsRef.current = getSlots;
    onWindowChangeRef.current = onWindowChange;
    onNearEdgeRef.current = onNearEdge;
    onMetricsRef.current = onMetrics;
  }, [getSlots, onWindowChange, onNearEdge, onMetrics]);

  const compute = useCallback(() => {
    const el = scrollerRef.current;
    const slots = getSlotsRef.current();
    if (!el || !slots || !slots.length) return;

    const scrollLeft = el.scrollLeft;
    const clientWidth = el.clientWidth;
    let start = findStartIndex(slots, scrollLeft - buffer);
    let end = findEndIndex(slots, scrollLeft + clientWidth + buffer);
    if (start < 0) start = 0;
    if (end > slots.length - 1) end = slots.length - 1;

    if (start !== lastWindow.current.start || end !== lastWindow.current.end) {
      lastWindow.current = { start, end };
      onWindowChangeRef.current(start, end);
    }

    if (onMetricsRef.current) onMetricsRef.current(scrollLeft, clientWidth);

    const maxScroll = el.scrollWidth - el.clientWidth;
    if (scrollLeft <= edgeThreshold) {
      onNearEdgeRef.current("left");
    } else if (scrollLeft >= maxScroll - edgeThreshold) {
      onNearEdgeRef.current("right");
    }
  }, [scrollerRef, buffer, edgeThreshold]);

  const scheduleCompute = useCallback(() => {
    if (rafId.current) return;
    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;
      compute();
    });
  }, [compute]);

  // Recompute window immediately (e.g. after a rebuild that changed geometry).
  // Resets the cached window so onWindowChange always fires.
  const recompute = useCallback(() => {
    lastWindow.current = { start: -1, end: -1 };
    compute();
  }, [compute]);

  useEffect(() => {
    const el = scrollerEl ?? scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      scheduleCompute();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    // Refresh the window for this element right away (it may be a fresh node
    // after a remount, with the previous window computed against the old one).
    scheduleCompute();
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, [scrollerRef, scrollerEl, scheduleCompute]);

  // Nearest slot left edge to a scroll position, clamped to the scrollable
  // range. Resting at a slot's left edge means the leftmost visible dot sits
  // flush against the viewport's left edge (never clipped mid-dot).
  const nearestSlotLeft = useCallback(
    (x) => {
      const el = scrollerRef.current;
      if (!el) return x;
      const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
      const clamped = Math.max(0, Math.min(x, maxScroll));
      const slots = getSlotsRef.current();
      if (!slots || !slots.length) return clamped;
      const idx = findEndIndex(slots, clamped);
      const a = slots[idx].left;
      const b = idx + 1 < slots.length ? slots[idx + 1].left : a;
      const nearest = clamped - a <= b - clamped ? a : b;
      return Math.max(0, Math.min(nearest, maxScroll));
    },
    [scrollerRef]
  );

  // State for the eased mouse-wheel animation (all mutated in place).
  const wheelState = useRef({ target: 0, raf: null, idleTimer: null, lastWritten: -1, lastTime: 0 });

  const cancelWheelAnim = useCallback(() => {
    const st = wheelState.current;
    if (st.raf) cancelAnimationFrame(st.raf);
    if (st.idleTimer) clearTimeout(st.idleTimer);
    st.raf = null;
    st.idleTimer = null;
    st.lastWritten = -1;
  }, []);

  // Device-aware wheel-to-horizontal. Mouse wheels (vertical-only, coarse) are
  // translated to an eased horizontal glide instead of discrete jumps;
  // trackpads keep both axes working natively so vertical page scroll is never
  // hijacked. Shift+wheel always goes horizontal as a deterministic fallback.
  // When the wheel goes idle the glide retargets to the nearest slot boundary,
  // so the motion always settles without clipping a dot.
  useEffect(() => {
    const el = scrollerEl ?? scrollerRef.current;
    if (!el) return;
    const st = wheelState.current;

    const step = (now) => {
      st.raf = null;
      // If something else moved the scroller mid-glide (e.g. the anchor
      // restore after a universe prepend shifts scrollLeft), carry the
      // remaining momentum over by shifting the target the same amount.
      if (st.lastWritten >= 0 && Math.abs(el.scrollLeft - st.lastWritten) > 1) {
        st.target += el.scrollLeft - st.lastWritten;
      }
      const dt = Math.min(Math.max(now - st.lastTime, 1), 64);
      st.lastTime = now;
      const diff = st.target - el.scrollLeft;
      if (Math.abs(diff) < 0.5) {
        el.scrollLeft = st.target;
        st.lastWritten = -1;
        return;
      }
      el.scrollLeft += diff * (1 - Math.exp(-dt / WHEEL_EASE_TAU));
      st.lastWritten = el.scrollLeft;
      st.raf = requestAnimationFrame(step);
    };

    const ensureAnim = () => {
      if (st.raf) return;
      st.lastTime = performance.now();
      st.lastWritten = -1;
      st.raf = requestAnimationFrame(step);
    };

    const onWheel = (e) => {
      const absX = Math.abs(e.deltaX);
      const absY = Math.abs(e.deltaY);

      // Horizontal-dominant: let native overflow-x handle it (trackpad swipe).
      if (absX > absY) return;
      if (absY === 0) return;

      // Vertical-dominant trackpad gesture: let it scroll the page normally,
      // but only when the page actually has vertical room to scroll. If it
      // does not, the gesture would otherwise do nothing (this also catches
      // macOS mouse wheels, whose small pixel deltas look like a trackpad).
      if (!e.shiftKey && isLikelyTrackpad(e) && pageScrollsVertically()) return;

      e.preventDefault();
      const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
      const base = st.raf ? st.target : el.scrollLeft;
      st.target = Math.max(0, Math.min(base + e.deltaY, maxScroll));
      ensureAnim();

      if (st.idleTimer) clearTimeout(st.idleTimer);
      st.idleTimer = setTimeout(() => {
        st.idleTimer = null;
        st.target = nearestSlotLeft(st.target);
        ensureAnim();
      }, WHEEL_IDLE_MS);
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      cancelWheelAnim();
    };
  }, [scrollerRef, scrollerEl, nearestSlotLeft, cancelWheelAnim]);

  // Settle on a slot boundary when native scrolling (trackpad swipe/momentum)
  // comes to rest. The correction is at most half a slot stride, applied as a
  // short smooth scroll, so it reads as the fling naturally landing clean.
  useEffect(() => {
    const el = scrollerEl ?? scrollerRef.current;
    if (!el) return;

    const settle = () => {
      // The wheel glide settles itself on a boundary; don't double-correct.
      if (wheelState.current.raf || wheelState.current.idleTimer) return;
      const snapped = nearestSlotLeft(el.scrollLeft);
      if (Math.abs(snapped - el.scrollLeft) > 1) {
        el.scrollTo({ left: snapped, behavior: "smooth" });
      }
    };

    if ("onscrollend" in el) {
      el.addEventListener("scrollend", settle);
      return () => el.removeEventListener("scrollend", settle);
    }

    // Fallback (e.g. Safari): treat a pause in scroll events as the end.
    let timer = null;
    const onScroll = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(settle, SCROLL_IDLE_MS);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (timer) clearTimeout(timer);
    };
  }, [scrollerRef, scrollerEl, nearestSlotLeft]);

  const scrollToPx = useCallback(
    (px, animate = true) => {
      const el = scrollerRef.current;
      if (!el) return;
      cancelWheelAnim();
      // Quantize so programmatic scrolls also end on a slot boundary.
      const target = nearestSlotLeft(px);
      el.scrollTo({ left: target, behavior: animate ? "smooth" : "auto" });
    },
    [scrollerRef, nearestSlotLeft, cancelWheelAnim]
  );

  return { compute, scheduleCompute, recompute, scrollToPx, snapToSlotPx: nearestSlotLeft };
}
